import type { Theme } from '@emotion/react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { BREAKPOINTS, mediaQuery, responsiveFontSize } from '@presentation/styles';

export const responsiveXPadding = (yPadding: number) => css`
  padding: ${yPadding}px 3rem;

  ${mediaQuery('desktop')} {
    padding: ${yPadding}px 2rem;
  }

  ${mediaQuery('tablet')} {
    padding: ${yPadding}px 1rem;
  }
`;

export const Container = styled.div`
  display: flex;
  flex-direction: row;
  background-color: ${(props) => props.theme.color.light.default};
`;

export const DesktopNav = styled.div`
  width: 300px;
  border-right: 1px solid ${(props) => props.theme.color.light.shadeLighter};
  height: calc(100vh - 72px);
  overflow: auto;

  ${mediaQuery(BREAKPOINTS.tablet)} {
    display: none;
  }
`;

export const Main = styled.div`
  flex: 1;
  height: calc(100vh - 72px);
  overflow: auto;
  padding: 20px 0;

  ${mediaQuery(BREAKPOINTS.tablet)} {
    padding: 0;
  }
`;

export const accordionWrapperStyle = () => css`
  border-radius: 0;
  box-shadow: none;
  margin: 0;
`;

export const accordionLabelStyle = (theme: Theme) => css`
  cursor: pointer;
  background: none;
  font-size: 18px;
  font-weight: 600;
  padding: 12px 0;
  border-radius: 0 !important;
  color: ${theme.color.dark.shade};
  border-top: 1px solid ${theme.color.light.shadeLighter};
  padding: 16px;
`;

export const SubLink = styled.div<{ active?: boolean }>`
  padding: 12px 32px;

  a {
    font-weight: 500;
    font-size: 14px;
    white-space: break-spaces;
    color: ${(props) =>
      props.active ? props.theme.color.blue.default : props.theme.color.dark.default};
  }
`;

export const TitleNavContainer = styled.div`
  ${responsiveXPadding(0)}

  ${mediaQuery(BREAKPOINTS.tablet)} {
    position: absolute;
    width: 100%;
    background-color: ${(props) => props.theme.color.light.default};
    z-index: 2;
    padding-top: 12px;
    padding-bottom: 12px;
  }
`;

export const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const MainTitle = styled.h1`
  ${responsiveFontSize(36, 4)}
  font-family: ${(props) => props.theme.font.alternate};
  font-weight: 600;
  color: ${(props) => props.theme.color.dark.shade};
`;

export const MobileNav = styled.div`
  max-height: 0;
  overflow: auto;
  margin-left: -1rem;
  margin-right: -1rem;
  transition: max-height 300ms ease-in-out;
  box-shadow: 0 10px 18px 0 rgba(0, 0, 0, 0.06);
`;

export const OpenButton = styled.label`
  cursor: pointer;
  font-size: 20px;
  display: flex;
`;

export const Input = styled.input`
  position: absolute;
  opacity: 0;
  z-index: -1;
  &:checked {
    ~ [data-puppy-nav] {
      max-height: 100vh;
      margin-top: 14px;
    }
  }
`;

export const SectionsContainer = styled.div`
  ${mediaQuery('tablet')} {
    margin-top: 60px;
    overflow: auto;
    height: calc(100% - 60px);
  }
`;

export const Section = styled.div`
  position: relative;

  &:after {
    content: '';
    position: absolute;
    top: -28px;
    right: 3rem;
    width: 200px;
    height: 200px;

    ${mediaQuery('tablet')} {
      right: 1rem;
    }
  }
`;

export const SectionTitle = styled.h2`
  ${responsiveFontSize(30, 2)}
  ${responsiveXPadding(0)}
  font-weight: 700;
  color: ${(props) => props.theme.color.dark.default};
  margin-top: 24px;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 3rem;
    width: 100px;
    height: 10px;
    z-index: 0;

    ${mediaQuery('tablet')} {
      left: 1rem;
    }
  }
`;

export const Image = styled.img`
  margin: 24px auto 0 auto;
  z-index: 1;
  display: flex;
  border-radius: 4px;
  max-width: 90%;
  width: 500px;

  ${mediaQuery('phone')} {
    width: auto;
  }
`;

export const SubSection = styled.div`
  ${responsiveXPadding(24)}
  border-bottom: 1px solid ${(props) => props.theme.color.light.shadeLighter};
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: -37px;
    right: 3rem;

    ${mediaQuery('tablet')} {
      right: 1rem;
    }
  }

  &:nth-of-type(1) {
    &:after {
      width: 75px;
      height: 75px;
    }
  }
  &:nth-of-type(2) {
    &:after {
      width: 75px;
      height: 75px;
    }
  }
`;

export const SubSectionTitle = styled.h3`
  font-size: 16px;
  color: ${(props) => props.theme.color.blue.default};
`;

export const SubSectionContent = styled.div`
  ul {
    list-style: disc !important;
    padding-left: 30px !important;
    margin: 0;
  }

  li {
    margin-top: 8px;
  }
`;

export const SubSectionButton = styled.div`
  text-align: center;
`;
